"use client";

// ArtifactDoc — one rendered document: a header (glyph · title · created/updated
// meta line) above the body.
//
// law_snappy — READING never mounts an editor. The body is ArtifactStatic (the
// same markdown, parsed and highlighted the way TipTap does, reshaped into the
// DOM TipTap would produce, so it looks identical — checked by
// scripts/prose-parity.mjs). The full TipTap editor (ArtifactProse) loads only
// when you press edit, and the static page stays on screen until it's ready, so
// the switch never flashes. Both are their own chunks: the reader is warmed while
// idle for everyone; the editor is warmed only for people who edit
// (lib/os/usage). Until the reader's chunk lands, the dependency-free <Markdown>
// stands in, and blurred neighbour cards pass `plain` to stay on it.

import { useEffect, useMemo, useState, type ComponentType } from "react";
import Markdown from "../../Markdown";
import ReadingBar from "./ReadingBar";
import { fmtDate, relTime, type OsNode } from "../../../lib/os/tree";
import { parseSkillDoc } from "../../../lib/skills/manifest";
import { noteUse } from "../../../lib/os/usage";

type ProseProps = {
  source: string;
  highlight?: string | null;
  className?: string;
  editable?: boolean;
  onChange?: (markdown: string) => void;
};

type StaticProps = { source: string; highlight?: string | null; className?: string };

// each chunk is loaded once per tab and kept at module level, so every later
// document opens straight into it — no fallback paint to be replaced a beat later.
let loadedStatic: ComponentType<StaticProps> | null = null;
let staticLoading: Promise<ComponentType<StaticProps>> | null = null;
function loadStatic(): Promise<ComponentType<StaticProps>> {
  staticLoading ??= import("./ArtifactStatic").then((m) => (loadedStatic = m.default as ComponentType<StaticProps>));
  staticLoading.catch(() => { staticLoading = null; });
  return staticLoading;
}
let loadedProse: ComponentType<ProseProps> | null = null;
let proseLoading: Promise<ComponentType<ProseProps>> | null = null;
export function loadArtifactEditor(): Promise<ComponentType<ProseProps>> {
  proseLoading ??= import("./ArtifactProse").then((m) => (loadedProse = m.default as ComponentType<ProseProps>));
  proseLoading.catch(() => { proseLoading = null; });
  return proseLoading;
}
/** the reader — warmed while idle by the shell, for everyone (everyone reads). */
export function warmArtifactReader(): void {
  void loadStatic().catch(() => { /* the reader falls back to markdown */ });
}

export default function ArtifactDoc({
  page,
  reader = false,
  plain = false,
  hideTitle = false,
  highlight,
  editable = false,
  onSaveDocument,
  overrideSource,
}: {
  page: OsNode;
  reader?: boolean;
  plain?: boolean;
  /** the expanded reader shows the title in the tab nav — don't repeat it in-doc */
  hideTitle?: boolean;
  highlight?: string | null;
  /** notion-like inline editing on the focused doc. */
  editable?: boolean;
  /** serialized markdown on each edit (upstream debounces the save). */
  onSaveDocument?: (markdown: string) => void;
  /** a locally-edited body to render instead of the DB copy (in-session persistence). */
  overrideSource?: string;
}) {
  const raw = overrideSource ?? (page.document || page.summary || "");
  // A skill document (law_skill_class) reads with its manifest as a quiet
  // ledger, not as raw frontmatter lines. Editing works on the FULL document —
  // the manifest is content, and stripping it from the editor would silently
  // drop it on save.
  const skill = useMemo(() => parseSkillDoc(raw, page.key), [raw, page.key]);
  const source = skill && !editable ? skill.body : raw;
  const [Static, setStatic] = useState<ComponentType<StaticProps> | null>(() => (plain ? null : loadedStatic));
  useEffect(() => {
    if (plain || loadedStatic) return;
    let alive = true;
    loadStatic()
      .then((S) => { if (alive) setStatic(() => S); })
      .catch(() => { /* keep the <Markdown> fallback if the chunk fails */ });
    return () => { alive = false; };
  }, [plain]);

  // the editor — only once editing is asked for. Remembered as a habit, so the
  // shell warms it next time for people who edit (and never for those who don't).
  const [Prose, setProse] = useState<ComponentType<ProseProps> | null>(() => loadedProse);
  useEffect(() => {
    if (!editable || plain) return;
    noteUse("edit");
    if (loadedProse) return; // already here — the render reads it directly
    let alive = true;
    loadArtifactEditor()
      .then((P) => { if (alive) setProse(() => P); })
      .catch(() => { /* stays read-only; the save path is unaffected */ });
    return () => { alive = false; };
  }, [editable, plain]);

  // the chunk may have landed for another document since this one mounted
  const Editor = Prose ?? loadedProse;
  const proseClass = `art-prose${reader ? " art-prose--reader" : ""}${editable ? " art-prose--editing" : ""}`;

  // The doc's status line — mirrors the navigator's system-info component
  // ("created …  ●  updated …"). Artifacts carry a mint date; other page kinds
  // (dimensions/nodes) show only "updated".
  const hasMeta = !!(page.createdAt || page.lastActiveAt);

  return (
    <div className={`art-doc${reader ? " art-doc--reader" : ""}`}>
      <div className="art-doc__head">
        {!hideTitle && (
          <div className="art-doc__titlewrap">
            <div className="art-doc__title">
              <span className="art-doc__glyph" aria-hidden>
                {page.symbol}
              </span>
              <span className="art-doc__name">{page.name}</span>
            </div>
            {/* how far through THIS document you are — under its title, exactly
                as wide as the title. Only the focused reader carries it; a
                peeking neighbour isn't being read. */}
            {reader && !plain && <ReadingBar docKey={page.key} />}
          </div>
        )}
        {hasMeta && (
          <div className="art-doc__meta">
            {page.createdAt && <span>created {fmtDate(page.createdAt)}</span>}
            {page.lastActiveAt && (
              <span><span className="ink-dot" aria-hidden />updated {relTime(page.lastActiveAt)}</span>
            )}
          </div>
        )}
      </div>
      {skill && !editable && (
        <div className="detail-rows sk-ledger art-doc__skill">
          <div className="detail-row"><span>kind</span><span>skill · paste its share link into any ai</span></div>
          <SkillVisibility projectId={page.projectId} path={page.path} />
          <div className="detail-row"><span>makes you able to</span><span>{skill.manifest.description}</span></div>
          {skill.manifest.when_to_load && (
            <div className="detail-row"><span>when to load it</span><span>{skill.manifest.when_to_load}</span></div>
          )}
          {skill.manifest.version && <div className="detail-row"><span>version</span><span>{skill.manifest.version}</span></div>}
          <div className="detail-row"><span>reading</span><span>{skill.minutes} min</span></div>
        </div>
      )}
      {editable && !plain && Editor ? (
        <Editor source={source} highlight={highlight} editable onChange={onSaveDocument} className={proseClass} />
      ) : !plain && Static ? (
        <Static source={source} highlight={editable ? null : highlight} className={proseClass} />
      ) : (
        <Markdown source={source} variant="artifact" style={{ fontSize: "clamp(1rem, 2.5vw, 1.08rem)", lineHeight: 1.68 }} />
      )}
    </div>
  );
}

/** WHO CAN SEE THIS SKILL — said out loud, because silence read as "maybe
 *  everyone". A skill in your innernet is private until you open a door on it:
 *  only you can read it, and every tool you connect can load it. One with a
 *  live share link says so, and names where it shows. */
function SkillVisibility({ projectId, path }: { projectId?: string; path: string }) {
  const [state, setState] = useState<"unknown" | "private" | "link" | "invite">("unknown");
  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    fetch(`/api/share?projectId=${encodeURIComponent(projectId)}`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { shares?: { path: string; visibility: string }[] } | null) => {
        if (!alive || !d) return;
        const mine = (d.shares ?? []).find((x) => x.path === path);
        setState(!mine ? "private" : mine.visibility === "link" ? "link" : "invite");
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [projectId, path]);
  if (state === "unknown") return null;
  return (
    <div className="detail-row">
      <span>who can see it</span>
      <span>
        {state === "private" && "private · only you, and the ai tools you connect. nobody else can open this page."}
        {state === "link" && "anyone with its link · it is listed on the community under your name."}
        {state === "invite" && "only the people you invited."}
      </span>
    </div>
  );
}
